import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { consultaAPI, formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Send, MessageCircleQuestion, Wrench, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Preguntarle algo a un agente **sin abrir un caso**.
 *
 * **Lo pidieron Guille, Carlos y Juan en dos minutos.** Lo que había era el Playground, y es la
 * mitad: un RAG de un solo tiro, con el prompt escrito a mano, sin el SOUL/RULES del agente, sin
 * rondas y sin las herramientas de exploración. Y restringido a admin y supervisor — o sea que
 * justamente los que preguntan no podían usarlo.
 *
 * **Preguntar no abre un caso, y la garantía está en el código**: el turno corre con el
 * presupuesto de tickets en cero. Un prompt que pide «no crees tickets» falla el día que el
 * modelo decide que éste sí corresponde, y ese día aparece un caso fantasma que ensucia los
 * indicadores y que nadie sabe de dónde salió.
 *
 * **Se muestra con qué lo averiguó.** En una consulta al investigador es la mitad del valor: sin
 * saber qué archivos miró, la respuesta no se puede verificar ni continuar.
 */
export default function ConsultaPage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const capsuleId = (user?.capsule_ids || [])[0] || 'sistemas';

  const [agentes, setAgentes] = useState([]);
  const [agente, setAgente] = useState('');
  const [pregunta, setPregunta] = useState('');
  const [hilo, setHilo] = useState([]);
  const [conversationId, setConversationId] = useState('');
  const [pensando, setPensando] = useState(false);
  const finDelHilo = useRef(null);

  const cargar = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data } = await consultaAPI.agentes(tenantId, capsuleId);
      setAgentes(data || []);
      if (data?.length && !agente) setAgente(data[0].agent_id);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudieron cargar los agentes');
    }
  }, [tenantId, capsuleId, agente]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { finDelHilo.current?.scrollIntoView({ behavior: 'smooth' }); }, [hilo, pensando]);

  const preguntar = async () => {
    const texto = pregunta.trim();
    if (!texto || !agente) return;
    setHilo((h) => [...h, { de: 'yo', texto }]);
    setPregunta('');
    setPensando(true);
    try {
      const { data } = await consultaAPI.preguntar(tenantId, capsuleId, {
        agent_id: agente, pregunta: texto, conversation_id: conversationId,
      });
      if (!data.ok) {
        setHilo((h) => [...h, { de: 'error', texto: data.error }]);
        return;
      }
      setConversationId(data.conversation_id || '');
      setHilo((h) => [...h, {
        de: data.agente, texto: data.respuesta, herramientas: data.herramientas || [],
      }]);
    } catch (err) {
      setHilo((h) => [...h, { de: 'error', texto: formatApiError(err) || 'No se pudo preguntar' }]);
    } finally {
      setPensando(false);
    }
  };

  const empezarDeNuevo = () => { setHilo([]); setConversationId(''); };

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col h-[calc(100vh-3rem)]" data-testid="consulta-page">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[#09090B]">CONSULTA</h1>
          <p className="text-xs text-zinc-400 mt-1 uppercase tracking-[0.15em]">
            Preguntar sin abrir un caso
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="elegir-agente"
            value={agente}
            onChange={(e) => { setAgente(e.target.value); empezarDeNuevo(); }}
            className="h-8 text-xs border border-brand-border rounded-sm px-2 bg-white"
          >
            {agentes.map((a) => (
              <option key={a.agent_id} value={a.agent_id}>{a.nombre}</option>
            ))}
          </select>
          {hilo.length > 0 && (
            <Button variant="outline" size="sm" onClick={empezarDeNuevo}
                    className="h-8 text-[10px] rounded-sm gap-1" data-testid="empezar-de-nuevo">
              <RotateCcw className="w-3 h-3" /> Empezar de nuevo
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {!hilo.length && (
          <Card className="rounded-sm border-dashed border-brand-border">
            <CardContent className="p-6 text-center space-y-1">
              <MessageCircleQuestion className="w-5 h-5 mx-auto text-zinc-300" />
              <p className="text-xs text-zinc-500">
                Preguntale lo que quieras. <b>Esto no abre un caso</b> ni le avisa a nadie.
              </p>
              <p className="text-[10px] text-zinc-400">
                {agentes.find((a) => a.agent_id === agente)?.descripcion}
              </p>
            </CardContent>
          </Card>
        )}

        {hilo.map((m, i) => (
          <div key={i} className={m.de === 'yo' ? 'flex justify-end' : ''}>
            <Card className={`rounded-sm max-w-[85%] ${
              m.de === 'yo' ? 'border-brand-primary/30 bg-blue-50/30'
              : m.de === 'error' ? 'border-red-200 bg-red-50' : 'border-brand-border'
            }`}>
              <CardContent className="p-3 space-y-2">
                {m.de !== 'yo' && (
                  <p className="text-[9px] uppercase tracking-wider text-zinc-400">
                    {m.de === 'error' ? 'no se pudo' : m.de}
                  </p>
                )}
                <p className={`text-xs whitespace-pre-wrap ${m.de === 'error' ? 'text-red-800' : 'text-zinc-800'}`}>
                  {m.texto}
                </p>
                {/* Con qué lo averiguó: sin eso la respuesta no se puede verificar ni continuar. */}
                {m.herramientas?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1 border-t border-brand-border/60">
                    <Wrench className="w-2.5 h-2.5 mt-1 text-zinc-400" />
                    {m.herramientas.map((h, j) => (
                      <Badge key={j} variant="outline" className="text-[9px] rounded-sm px-1 py-0">
                        {typeof h === 'string' ? h : h?.tool || h?.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ))}

        {pensando && (
          <p className="text-[11px] text-zinc-500 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Buscando…
          </p>
        )}
        <div ref={finDelHilo} />
      </div>

      <div className="pt-3 flex gap-2 items-end">
        <textarea
          data-testid="pregunta"
          value={pregunta}
          onChange={(e) => setPregunta(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); preguntar(); }
          }}
          placeholder="¿Dónde se valida el CUIT al dar de alta un cliente?"
          className="flex-1 border border-brand-border rounded-sm p-2 text-xs h-16 focus:ring-1 focus:ring-brand-primary focus:outline-none resize-none"
        />
        <Button
          data-testid="preguntar"
          onClick={preguntar}
          disabled={pensando || !pregunta.trim() || !agente}
          className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm h-16 px-4"
        >
          {pensando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
